import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLanguage } from '@/context/LanguageContext';
import { restaurantInfo } from '@/data/menuData';
import { Calendar, Clock, Users, User, Phone, MessageSquare, Send, X } from 'lucide-react';

const ReservationModal = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    date: '',
    time: '',
    guests: '2',
    notes: '',
  });

  const texts = {
    tr: {
      title: 'Rezervasyon Yap',
      subtitle: 'Masanızı şimdiden ayırtın',
      name: 'Ad Soyad',
      namePlaceholder: 'Adınızı giriniz',
      phone: 'Telefon',
      phonePlaceholder: '05XX XXX XX XX',
      date: 'Tarih',
      time: 'Saat',
      guests: 'Kişi Sayısı',
      guestOptions: ['1 Kişi', '2 Kişi', '3 Kişi', '4 Kişi', '5 Kişi', '6 Kişi', '7 Kişi', '8+ Kişi'],
      notes: 'Notlar (Opsiyonel)',
      notesPlaceholder: 'Özel isteklerinizi belirtebilirsiniz...',
      submit: 'Rezervasyon Yap',
      sending: 'Gönderiliyor...',
      requiredFields: 'Lütfen zorunlu alanları doldurunuz',
    },
    en: {
      title: 'Make Reservation',
      subtitle: 'Reserve your table now',
      name: 'Full Name',
      namePlaceholder: 'Enter your name',
      phone: 'Phone',
      phonePlaceholder: '05XX XXX XX XX',
      date: 'Date',
      time: 'Time',
      guests: 'Number of Guests',
      guestOptions: ['1 Person', '2 People', '3 People', '4 People', '5 People', '6 People', '7 People', '8+ People'],
      notes: 'Notes (Optional)',
      notesPlaceholder: 'You can specify your special requests...',
      submit: 'Make Reservation',
      sending: 'Sending...',
      requiredFields: 'Please fill in the required fields',
    },
  };

  const t = texts[language];

  const timeSlots = [
    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30',
    '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00', '22:30', '23:00'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.date || !formData.time) {
      alert(t.requiredFields);
      return;
    }

    // Create WhatsApp message
    const guestText = t.guestOptions[parseInt(formData.guests) - 1] || formData.guests;
    
    const message = language === 'tr' 
      ? `🍽️ *YENİ REZERVASYON*

👤 *Ad Soyad:* ${formData.name}
📞 *Telefon:* ${formData.phone}
📅 *Tarih:* ${formData.date}
🕐 *Saat:* ${formData.time}
👥 *Kişi Sayısı:* ${guestText}
${formData.notes ? `📝 *Notlar:* ${formData.notes}` : ''}

_GOMŞU RESTORAN Online Rezervasyon_`
      : `🍽️ *NEW RESERVATION*

👤 *Name:* ${formData.name}
📞 *Phone:* ${formData.phone}
📅 *Date:* ${formData.date}
🕐 *Time:* ${formData.time}
👥 *Guests:* ${guestText}
${formData.notes ? `📝 *Notes:* ${formData.notes}` : ''}

_GOMŞU RESTORAN Online Reservation_`;

    // Encode message for URL
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${restaurantInfo.whatsapp}?text=${encodedMessage}`;

    // Reset form and close modal first
    setFormData({
      name: '',
      phone: '',
      date: '',
      time: '',
      guests: '2',
      notes: '',
    });
    onClose();

    // Open WhatsApp directly
    window.location.href = whatsappUrl;
  };

  // Get today's date in YYYY-MM-DD format for min date
  const today = new Date().toISOString().split('T')[0];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-primary/20 max-w-md mx-auto p-0 overflow-hidden rounded-2xl max-h-[90vh] overflow-y-auto">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-charcoal-light to-charcoal p-6 border-b border-primary/20">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-primary text-center">
              {t.title}
            </DialogTitle>
            <p className="text-muted-foreground text-center text-sm mt-1">{t.subtitle}</p>
          </DialogHeader>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-foreground flex items-center gap-2">
              <User className="w-4 h-4 text-primary" />
              {t.name} <span className="text-secondary">*</span>
            </Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder={t.namePlaceholder}
              className="bg-muted border-border/50 focus:border-primary text-foreground placeholder:text-muted-foreground"
              required
            />
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-foreground flex items-center gap-2">
              <Phone className="w-4 h-4 text-primary" />
              {t.phone} <span className="text-secondary">*</span>
            </Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder={t.phonePlaceholder}
              className="bg-muted border-border/50 focus:border-primary text-foreground placeholder:text-muted-foreground"
              required
            />
          </div>

          {/* Date and Time Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Date */}
            <div className="space-y-2">
              <Label htmlFor="date" className="text-foreground flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                {t.date} <span className="text-secondary">*</span>
              </Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                min={today}
                className="bg-muted border-border/50 focus:border-primary text-foreground [color-scheme:dark]"
                required
              />
            </div>

            {/* Time */}
            <div className="space-y-2">
              <Label htmlFor="time" className="text-foreground flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                {t.time} <span className="text-secondary">*</span>
              </Label>
              <Select value={formData.time} onValueChange={(value) => handleInputChange('time', value)}>
                <SelectTrigger className="bg-muted border-border/50 focus:border-primary text-foreground">
                  <SelectValue placeholder="--:--" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border/50 max-h-48">
                  {timeSlots.map((time) => (
                    <SelectItem key={time} value={time} className="text-foreground hover:bg-muted">
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Guests */}
          <div className="space-y-2">
            <Label htmlFor="guests" className="text-foreground flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              {t.guests} <span className="text-secondary">*</span>
            </Label>
            <Select value={formData.guests} onValueChange={(value) => handleInputChange('guests', value)}>
              <SelectTrigger className="bg-muted border-border/50 focus:border-primary text-foreground">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border/50">
                {t.guestOptions.map((option, index) => (
                  <SelectItem key={index + 1} value={String(index + 1)} className="text-foreground hover:bg-muted">
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-foreground flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-primary" />
              {t.notes}
            </Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder={t.notesPlaceholder}
              rows={3}
              className="bg-muted border-border/50 focus:border-primary text-foreground placeholder:text-muted-foreground resize-none"
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full py-6 text-base font-medium gradient-gold text-primary-foreground rounded-xl shadow-gold hover:shadow-gold-lg transition-all duration-300 flex items-center justify-center gap-2"
          >
            <Send className="w-5 h-5" />
            {t.submit}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReservationModal;
